import { useState, useEffect, useCallback } from "react";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "./ui/popover";
import { Copy, Share, ArrowClockwise, LinkSimple, Check } from "@phosphor-icons/react";
import { http, formatApiError } from "../lib/api";
import { toast } from "sonner";

export default function ProjectShareBar({ projectName, onRegenerated }) {
  const [project, setProject] = useState(null);
  const [copied, setCopied] = useState(false);
  const [regenerating, setRegenerating] = useState(false);

  const shareUrl = project
    ? `${window.location.origin}/c/${project.share_token}`
    : "";

  const load = useCallback(async () => {
    try {
      const { data } = await http.get("/projects");
      const found = data.find((p) => p.name === projectName);
      setProject(found || null);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, [projectName]);

  useEffect(() => { if (projectName) load(); }, [projectName, load]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Client link copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Could not copy — please copy manually");
    }
  };

  const regenerate = async () => {
    if (!window.confirm("Generate a new client link? The old one will stop working immediately.")) return;
    setRegenerating(true);
    try {
      await http.post(`/projects/${encodeURIComponent(projectName)}/regenerate`);
      toast.success("New link generated");
      await load();
      if (onRegenerated) onRegenerated();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setRegenerating(false);
    }
  };

  if (!project) return null;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          data-testid={`share-project-${projectName}`}
          className="flex items-center gap-2 px-3 py-1.5 border border-zinc-300 bg-white hover:bg-zinc-900 hover:text-white hover:border-zinc-900 label-overline text-zinc-700 transition-colors"
        >
          <Share size={12} weight="bold" /> Share Client Link
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 rounded-none border-2 border-zinc-900 shadow-brutal p-0">
        <div className="border-b border-zinc-300 bg-zinc-900 text-white p-4">
          <div className="label-overline text-orange-500 flex items-center gap-1.5">
            <LinkSimple size={12} weight="bold" /> Public Client Link
          </div>
          <div className="font-display font-bold text-lg tracking-tight mt-1 truncate">{projectName}</div>
        </div>
        <div className="p-4 space-y-3">
          <div className="label-overline text-zinc-500">Share this URL with your client</div>
          <div className="flex items-stretch border border-zinc-400">
            <input
              readOnly
              value={shareUrl}
              data-testid="share-url-input"
              className="flex-1 px-3 py-2 text-xs font-mono bg-zinc-50 text-zinc-800 outline-none min-w-0 truncate"
              onFocus={(e) => e.target.select()}
            />
            <button
              onClick={copy}
              data-testid="copy-share-url"
              className="px-3 border-l border-zinc-400 bg-zinc-900 text-white hover:bg-blue-600 flex items-center gap-1.5 label-overline"
            >
              {copied ? <Check size={12} weight="bold" /> : <Copy size={12} weight="bold" />}
              {copied ? "Copied" : "Copy"}
            </button>
          </div>

          <div className="grid grid-cols-3 gap-0 border border-zinc-300 bg-zinc-50">
            <StatMini label="Progress" value={`${project.progress_pct}%`} />
            <StatMini label="Tasks" value={`${project.complete_tasks}/${project.total_tasks}`} />
            <StatMini label="Billed" value={
              project.total_value
                ? `${Math.round((project.billed_amount / project.total_value) * 100)}%`
                : "—"
            } />
          </div>

          <div className="flex items-center justify-between pt-2 border-t border-zinc-200">
            <a
              href={shareUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="preview-share-url"
              className="text-xs text-blue-600 hover:underline font-mono"
            >
              Preview →
            </a>
            <button
              onClick={regenerate}
              disabled={regenerating}
              data-testid="regenerate-share-url"
              className="flex items-center gap-1.5 label-overline text-zinc-500 hover:text-red-600"
            >
              <ArrowClockwise size={11} weight="bold" />
              {regenerating ? "Regenerating…" : "Revoke & New Link"}
            </button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function StatMini({ label, value }) {
  return (
    <div className="border-r border-zinc-300 last:border-r-0 p-2 text-center">
      <div className="label-overline text-zinc-500">{label}</div>
      <div className="font-display font-bold text-zinc-900 text-sm mt-1">{value}</div>
    </div>
  );
}
